import { useEffect, useMemo, useRef, useState } from "react";
import { renderListTemplate } from "../../api";
import { localBackend } from "../../api/adapters/local-backend";
import { useAppStore } from "../../state/app-store";
import { filterClass } from "../../utils/class-utils";
import { getSelectedBackend } from "../../utils/easyblocks-config/config";
import type { ListTemplateSectionProps } from "./types";

const LOCATION_SEARCH_CHANGE_EVENT = "easyblocks:location-search-change";

function getCurrentPageSearch(): string {
  if (typeof window === "undefined") return "";
  return window.location.search;
}

function buildUrlFromGetForm(form: HTMLFormElement): URL {
  const currentUrl = new URL(window.location.href);
  const action = form.getAttribute("action")?.trim();
  const nextUrl = new URL(action || window.location.href, window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  const formData = new FormData(form);
  const fieldNames = new Set<string>();

  Array.from(form.elements).forEach((element) => {
    if (
      !(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLSelectElement) &&
      !(element instanceof HTMLTextAreaElement) &&
      !(element instanceof HTMLButtonElement)
    ) {
      return;
    }
    if (!element.name || element.disabled) return;
    if (element instanceof HTMLButtonElement) return;
    if (element instanceof HTMLInputElement && ["submit", "button", "reset", "file"].includes(element.type)) {
      return;
    }
    fieldNames.add(element.name);
  });

  fieldNames.forEach((name) => params.delete(name));

  for (const [key, value] of formData.entries()) {
    if (typeof value === "string") {
      params.append(key, value);
    }
  }

  nextUrl.search = params.toString();
  return nextUrl;
}

export function ListTemplateSection(props: ListTemplateSectionProps) {
  const {
    bgClass = "text-bg-white",
    paddingYClass = "py-section-md",
    sectionClassName,
    listTemplateName = "",
  } = props;

  const isEditor = useAppStore((state) => state.runtimeMode === "editor");
  const pageItemId = useAppStore((state) => state.documentId);
  const htmlContainerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [html, setHtml] = useState("");
  const [pageSearch, setPageSearch] = useState(getCurrentPageSearch);

  const normalizedTemplateName = useMemo(() => String(listTemplateName ?? "").trim(), [listTemplateName]);

  const sectionClasses = [
    bgClass,
    filterClass(paddingYClass),
    ...(Array.isArray(sectionClassName) ? sectionClassName : [sectionClassName]).flatMap((className) =>
      String(className ?? "")
        .trim()
        .split(/\s+/)
    ),
  ]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncSearchFromLocation = () => {
      setPageSearch(getCurrentPageSearch());
    };

    window.addEventListener("popstate", syncSearchFromLocation);
    window.addEventListener(LOCATION_SEARCH_CHANGE_EVENT, syncSearchFromLocation);

    return () => {
      window.removeEventListener("popstate", syncSearchFromLocation);
      window.removeEventListener(LOCATION_SEARCH_CHANGE_EVENT, syncSearchFromLocation);
    };
  }, []);

  useEffect(() => {
    const container = htmlContainerRef.current;
    if (!container || typeof window === "undefined") return;

    const handleSubmit = (event: Event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const method = (form.getAttribute("method") || "get").toLowerCase();
      if (method !== "get") return;

      event.preventDefault();
      const nextUrl = buildUrlFromGetForm(form);
      const nextHref = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      if (nextHref !== currentHref) {
        window.history.pushState({}, "", nextHref);
      }

      window.dispatchEvent(new Event(LOCATION_SEARCH_CHANGE_EVENT));
    };

    container.addEventListener("submit", handleSubmit, true);
    return () => {
      container.removeEventListener("submit", handleSubmit, true);
    };
  }, [html]);

  useEffect(() => {
    let isCancelled = false;

    if (!normalizedTemplateName) {
      setHtml("");
      setErrorText("Missing list template name.");
      setIsLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    if (getSelectedBackend() === localBackend) {
      setIsLoading(false);
      setErrorText(null);
      setHtml(
        `<div class="small text-body-secondary">List template preview is not available in local mode. Template: <code>${normalizedTemplateName}</code></div>`
      );
      return () => {
        isCancelled = true;
      };
    }

    setIsLoading(true);
    setErrorText(null);

    void renderListTemplate({
      KD_list_tpl_item_name: normalizedTemplateName,
      pageSearch,
      page_item_id: pageItemId ?? undefined,
    })
      .then((res) => {
        if (isCancelled) return;
        if (Number(res?.error) > 0) {
          setHtml("");
          setErrorText(res?.error_msg || "List template render failed.");
          setIsLoading(false);
          return;
        }
        setHtml(String(res?.html ?? ""));
        setErrorText(null);
        setIsLoading(false);
      })
      .catch((err: any) => {
        if (isCancelled) return;
        setHtml("");
        setErrorText(err?.message ?? "List template render failed.");
        setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [normalizedTemplateName, pageItemId, pageSearch]);

  if (!isEditor && !isLoading && !errorText && !html) {
    return null;
  }

  if (isLoading) {
    return (
      <section className={["section", sectionClasses].filter(Boolean).join(" ").trim()}>
        <div className="container-xl">
          <div className="d-flex align-items-center gap-2 small text-body-secondary">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            Loading...
          </div>
        </div>
      </section>
    );
  }

  if (errorText) {
    return (
      <section className={["section", sectionClasses].filter(Boolean).join(" ").trim()}>
        <div className="container-xl">
          <div className="alert alert-warning py-2 px-3 small mb-0" role="alert">
            {errorText}
          </div>
        </div>
      </section>
    );
  }

  if (html) {
    return <div ref={htmlContainerRef} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return null;
}

export default ListTemplateSection;
